import pluralize from "pluralize";

import {
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

import { type SalesMetricV2 } from "../types";

export function makeCustomGoal({
  goalId,
  goalName,
  attributionModelLabel,
  accountingMode,
  attributionWindow,
}: {
  goalId: string;
  goalName: string;
  attributionModelLabel: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = `customGoal:${goalId}`;
  const name = pluralize(goalName);

  return {
    id,
    name,
    fullName:
      name + (accountingMode === "accrual" ? ` / (${attributionWindow}d)` : ""),
    description: `${name} attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics.customGoals?.[goalId];
    },
  };
}

export function makeCustomGoalCost({
  goalId,
  goalName,
  accountingMode,
  attributionWindow,
}: {
  goalId: string;
  goalName: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = `customGoalCost:${goalId}`;
  const name = `Cost per ${goalName}`;

  return {
    id,
    name,
    fullName:
      name + (accountingMode === "accrual" ? ` / (${attributionWindow}d)` : ""),
    description: `Average cost of new visits that end in an ${goalName}.`,
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics.customGoalCosts?.[goalId];
    },
  };
}

export function makeCustomGoalRate({
  goalId,
  goalName,
  accountingMode,
  attributionWindow,
}: {
  goalId: string;
  goalName: string;
  accountingMode: INBAccountingMode;
  attributionWindow: IMCAttributionWindowDays;
}): SalesMetricV2 {
  const id = `customGoalRate:${goalId}`;
  const name = `${goalName} Rate`;

  return {
    id,
    name,
    fullName:
      name + (accountingMode === "accrual" ? ` / (${attributionWindow}d)` : ""),
    description: `Percentage of new visits that end in an ${goalName}.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics.customGoalRates?.[goalId];
    },
  };
}

export function makeCustomGoalLtv({
  goalId,
  goalName,
  attributionModelLabel,
  isDashboardV2,
}: {
  goalId: string;
  goalName: string;
  attributionModelLabel: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = `customGoalLtv:${goalId}`;
  const name = isDashboardV2
    ? `${pluralize(goalName)} (∞)`
    : `LTV ${pluralize(goalName)}`;

  return {
    id,
    name,
    fullName: `${name} / (∞)`,
    description: `${name} attributable to this ad using the ${attributionModelLabel} attribution model.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "decimal",
    getValue(metrics) {
      return metrics.customGoalsLtv?.[goalId];
    },
    isLtv: true,
  };
}

export function makeCustomGoalCostLtv({
  goalId,
  goalName,
  isDashboardV2,
}: {
  goalId: string;
  goalName: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = `customGoalCostLtv:${goalId}`;
  const name = isDashboardV2
    ? `Cost per ${goalName} (∞)`
    : `LTV Cost per ${goalName}`;

  return {
    id,
    name,
    fullName: `${name} / (∞)`,
    description: `Average cost of new visits that end in an ${goalName}.`,
    category: "northbeam",
    positiveTrend: "down",
    format: "dollars",
    getValue(metrics) {
      return metrics.customGoalCostsLtv?.[goalId];
    },
    isLtv: true,
  };
}

export function makeCustomGoalRateLtv({
  goalId,
  goalName,
  isDashboardV2,
}: {
  goalId: string;
  goalName: string;
  isDashboardV2: boolean;
}): SalesMetricV2 {
  const id = `customGoalRateLtv:${goalId}`;
  const name = isDashboardV2 ? `${goalName} Rate (∞)` : `LTV ${goalName} Rate`;

  return {
    id,
    name,
    fullName: `${name} / (∞)`,
    description: `Percentage of new visits that end in an ${goalName}.`,
    category: "northbeam",
    positiveTrend: "up",
    format: "percentage",
    getValue(metrics) {
      return metrics.customGoalRatesLtv?.[goalId];
    },
    isLtv: true,
  };
}
